import { useEffect, useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchTenants } from 'common/services/api/Tenants/Tenants.thunks';
import { ApplicationSuite,AppSuiteTenant } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

type useFetchTenantsProps = {
	appSuite: ApplicationSuite | null,
	onChange: (event: unknown, value: AppSuiteTenant | null) => void,
	disabled: boolean,
};

export const useFetchTenants = ({
    appSuite,
    onChange,
    disabled
}: useFetchTenantsProps) => {
    const [tenants, setTenants] = useState<AppSuiteTenant[]>([]);
    const [singleTenant, setSingleTenant] = useState<AppSuiteTenant | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const _isSingleTenant = (fetchedTenant: AppSuiteTenant[]): boolean => {
        return fetchedTenant.length === 1;
    };

    const _processFetchTenantsRequestSuccess = (tenantsResponse: AppSuiteTenant[]) => {
        if(_isSingleTenant(tenantsResponse)) {
            const fetchedTenant = tenantsResponse[0];
            setSingleTenant(fetchedTenant);
            onChange?.(null, fetchedTenant);
            setTenants([]);
        } else {
            setSingleTenant(null);
            setTenants(tenantsResponse);
        }
    };

    const _processFetchTenantsRequestFailure = (error: unknown) => {
        const err = error as Error;
        setSingleTenant(null);
        setTenants([]);
        setProcessing(false);
        handleListFetchError(extractStatusCode(err.message), 'Tenant', enqueueSnackbar);
    };

    useEffect(() => {
        if(!disabled && appSuite?.id) {
            setProcessing(true);
            dispatch(fetchTenants({ id: appSuite?.id ?? '' }))
                .unwrap()
                .then(response => {
                    _processFetchTenantsRequestSuccess(response);
                })
                .catch((error) => {
                    _processFetchTenantsRequestFailure(error);
                })
                .finally(() =>  setProcessing(false));
        }
    }, [appSuite?.id, disabled]);

    return {
        singleTenant,
        tenants,
        processing,
    };
};
